import { useEffect, useRef } from "react";
import { AlertTriangle, FileText, Loader2, WalletCards } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getActiveAnnouncements } from "@/api/settings.api";
import { getPatientBalance } from "@/api/patient-billing.api";
import type { Patient } from "@/types";
import { formatCents } from "@/lib/utils";

interface PatientBookingAlertDialogProps {
  patient: Patient | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContinue: (patient: Patient) => void;
  onOpenChart?: (patient: Patient) => void;
}

function formatCurrency(cents: number): string {
  return formatCents(cents);
}

/**
 * Checks staff announcements and outstanding balance before a patient is
 * selected for booking. Patients without warnings continue automatically.
 */
export function PatientBookingAlertDialog({
  patient,
  open,
  onOpenChange,
  onContinue,
  onOpenChart,
}: PatientBookingAlertDialogProps) {
  const continuedPatientId = useRef<string | null>(null);
  const patientId = patient?.id;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["patient-booking-warnings", patientId],
    queryFn: async () => {
      const [announcements, balance] = await Promise.all([
        getActiveAnnouncements(patientId!),
        getPatientBalance(patientId!),
      ]);
      return { announcements, balance };
    },
    enabled: open && !!patientId,
    staleTime: 30_000,
  });

  const outstandingBalance = data?.balance.outstanding_balance ?? 0;
  const announcements = data?.announcements ?? [];
  const hasWarnings = outstandingBalance > 0 || announcements.length > 0;

  useEffect(() => {
    if (!open || !patient || !data || hasWarnings) return;
    if (continuedPatientId.current === patient.id) return;
    continuedPatientId.current = patient.id;
    onOpenChange(false);
    onContinue(patient);
  }, [data, hasWarnings, onContinue, onOpenChange, open, patient]);

  useEffect(() => {
    if (!open) continuedPatientId.current = null;
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Booking notice
          </DialogTitle>
          <DialogDescription>
            {patient
              ? `Review notices for ${patient.first_name} ${patient.last_name} before booking.`
              : "Review patient notices before booking."}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking account and staff notices…
          </div>
        ) : isError ? (
          <div className="rounded-2xl border border-warning/30 bg-warning/10 p-4 text-sm">
            Patient notices could not be checked. Confirm details before continuing.
          </div>
        ) : (
          <div className="max-h-80 space-y-3 overflow-y-auto">
            {outstandingBalance > 0 && (
              <div className="flex gap-3 rounded-2xl border border-warning/40 bg-warning/10 p-4">
                <WalletCards className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                <div>
                  <p className="text-sm font-black text-foreground">Outstanding balance</p>
                  <p className="mt-1 text-lg font-black text-warning">
                    {formatCurrency(outstandingBalance)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Follow your organization’s payment policy while scheduling.
                  </p>
                </div>
              </div>
            )}

            {announcements.map((announcement) => (
              <div
                key={announcement.id}
                className="flex gap-3 rounded-2xl border border-border bg-muted/40 p-4"
              >
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                    {announcement.category}
                  </p>
                  <p className="mt-1 text-sm font-black text-foreground">
                    {announcement.title}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-foreground/80">
                    {announcement.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {patient && onOpenChart && (
            <Button type="button" variant="outline" onClick={() => onOpenChart(patient)}>
              Open chart
            </Button>
          )}
          <Button
            type="button"
            disabled={isLoading}
            onClick={() => {
              if (!patient) return;
              onOpenChange(false);
              onContinue(patient);
            }}
          >
            Continue booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
